import React from 'react';
import PropTypes from 'prop-types';

const splitTextAndKeys = (text, replacements, defaultColor) => {
  let str = text;
  replacements.forEach((_, i) => {
    str = str.replace(`%${i + 1}$s`, '{{*}}');
  });

  let keyIdx = 0;
  const list = [];
  str.split('{{*}}').forEach((s, idx, splitList) => {
    if (s.length === 0) {
      if (keyIdx < replacements.length) {
        list.push({
          text: replacements[keyIdx].text,
          color: replacements[keyIdx].color,
        });
        keyIdx += 1;
      }
    } else {
      list.push({
        text: s,
        color: defaultColor,
      });
      if (idx + 1 < splitList.length && keyIdx < replacements.length) {
        list.push({
          text: replacements[keyIdx].text,
          color: replacements[keyIdx].color,
        });
        keyIdx += 1;
      }
    }
  });
  return list;
};

const reduceTextAndKeys = (list, idx, entry) => {
  if (idx < 0) {
    return entry;
  }
  const newEntry = {
    text: list[idx].text,
    color: list[idx].color,
    child: entry,
  };
  return reduceTextAndKeys(list, idx - 1, newEntry);
};

/**
 *  Adaptation of the MTextColored component from the mobile project
 *  It takes a string and a list of replacements and returns a React component
 *  with the string and the replacements colored
 *
 *  Changes from mobile: replacements are by order and not key because that's what the web
 *  translation library expects, we also pass a className to component which should apply down the tree
 *  so there's no need to pass a style prop to every child
 *
 * @param {String} className
 * @param {Color} defaultColor
 * @param {String} text
 * @param {[{ text, color }]} replacements
 * @returns {Element}
 * @constructor
 */
export default function ColoredTranslation({
  className,
  defaultColor,
  text,
  replacements,
}) {
  const list = splitTextAndKeys(text, replacements, defaultColor);
  const element = reduceTextAndKeys(list, list.length - 1, null);

  if (!element) {
    return null;
  }

  return (
    <span className={className} style={{ color: element.color }}>
      {element.text}
      {element.child && element.child.text && (
        <ColoredTranslationChild
          text={element.child.text}
          color={element.child.color}
          child={element.child.child}
        />
      )}
    </span>
  );
}

const ColoredText = {
  text: PropTypes.string.isRequired,
  color: PropTypes.string,
};
ColoredText.child = PropTypes.shape(ColoredText);

ColoredTranslation.propTypes = {
  className: PropTypes.string,
  defaultColor: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  replacements: PropTypes.arrayOf(PropTypes.shape(ColoredText)).isRequired,
};

ColoredTranslation.defaultProps = {
  className: undefined,
};

function ColoredTranslationChild({ text, color, child }) {
  return (
    <span style={{ color }}>
      {text}
      {child && child.text && (
        <ColoredTranslationChild
          text={child.text}
          color={child.color}
          child={child.child}
        />
      )}
    </span>
  );
}

ColoredTranslationChild.propTypes = {
  text: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  child: PropTypes.shape(ColoredText),
};

ColoredTranslationChild.defaultProps = {
  child: null,
};
